import React, { useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageCircle, ArrowUpRight, FileText, Receipt, ChevronLeft, CreditCard } from 'lucide-react';
import PageHeading from '../components/PageHeading';
import { getPaymentUrl, createPayNowPayment, createCryptoPayment } from '../api/payment';

function getRange(amount) {
  const num = parseFloat(amount);
  if (!num || num <= 0) return null;
  const ceiling = Math.ceil(num / 100) * 100;
  const lower = ceiling - 100;
  return {
    lower,
    upper: ceiling,
    fee: ceiling * 0.04,
    label: `¥${lower === 0 ? 0 : lower + 1} - ¥${ceiling}`,
  };
}

const AMOUNT_RE = /^\d+(\.\d+)?$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PRICE_RANGES = Array.from({ length: 30 }, (_, i) => ({
  productId: 336 + i,
  min: i === 0 ? 0 : i * 100 + 1,
  max: (i + 1) * 100,
  label: i === 0 ? '0 - 100' : `${i * 100 + 1} - ${(i + 1) * 100}`,
}));

function matchProduct(amount) {
  const num = parseFloat(amount);
  if (!num || num <= 0) return null;
  if (num > 3000) return 'overflow';
  return PRICE_RANGES.find((r) => num <= r.max);
}

const DEFAULT_PAYMENT_DOMAIN = 'getpro.plus';
const DIRECT_PAY_URL_METHODS = new Set(['alipay', 'alipay-hk', 'credit-card']);

const getPaymentDomain = () => {
  const hostname = window.location.hostname?.toLowerCase() || '';
  const localDomains = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);
  if (!hostname || localDomains.has(hostname) || hostname.endsWith('.localhost')) {
    return DEFAULT_PAYMENT_DOMAIN;
  }
  return hostname;
};

const getRedirectUrlByMethod = (method, response) => {
  if (DIRECT_PAY_URL_METHODS.has(method)) return response?.data?.payUrl || response?.payUrl || '';
  if (method === 'usdt') return response?.data?.invoiceUrl || response?.invoiceUrl || '';
  return response?.data?.payUrl || response?.payUrl || '';
};

const isPaymentRequestSuccessful = (method, response, redirectUrl) => {
  if (!redirectUrl) return false;
  if (DIRECT_PAY_URL_METHODS.has(method)) return true;
  return response?.status === '1' || response?.status === 1 || response?.success === true || response?.code === 100;
};

const getChannelNumber = (method) => {
  const channelMap = { 'alipay': 1, 'alipay-hk': 2, 'credit-card': 10 };
  return channelMap[method] || 1;
};

function PaidVoucherForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ orderId: '', invoiceInfo: '', amount: '', email: '', productId: null });

  const [errors, setErrors] = useState({ amount: '', email: '' });
  const [amountHint, setAmountHint] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('alipay');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('fetching');
  const hintTimer = useRef(null);

  const paymentIcons = {
    'alipay': `${process.env.PUBLIC_URL}/images/payment/alipay.jpg`,
    'alipay-hk': `${process.env.PUBLIC_URL}/images/payment/alipay_hk.jpg`,
    'credit-card': `${process.env.PUBLIC_URL}/images/payment/credicard.jpg`,
    'paynow': `${process.env.PUBLIC_URL}/images/payment/paynow.jpg`,
    'usdt': `${process.env.PUBLIC_URL}/images/payment/usdt.jpg`,
  };

  const paymentOptions = [
    { value: 'alipay', label: '支付宝', gridClass: 'col-span-2 md:col-span-5', iconClass: 'w-8 h-8', recommended: false },
    { value: 'alipay-hk', label: '支付宝香港', gridClass: 'col-span-1 md:col-span-3', iconClass: 'w-6 h-6' },
    { value: 'credit-card', label: '信用卡', subLabel: 'Visa/Master', gridClass: 'col-span-1 md:col-span-4', iconClass: 'w-6 h-6' },
    { value: 'paynow', label: 'PayNow', gridClass: 'col-span-1 md:col-span-6', iconClass: 'w-6 h-6' },
    { value: 'usdt', label: 'USDT', gridClass: 'col-span-1 md:col-span-6', iconClass: 'w-6 h-6' },
  ];

  const getPaymentCardClasses = (method) => `group relative overflow-hidden border rounded-xl cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-brand ${paymentMethod === method ? 'border-brand bg-brand-soft' : 'border-line bg-white hover:border-brand-line'}`;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;
    if (val === '' || /^[\d.]*$/.test(val)) {
      const matched = matchProduct(val);
      const productId = matched && matched !== 'overflow' ? matched.productId : null;
      setForm((f) => ({ ...f, amount: val, productId }));
      const isOverflow = matched === 'overflow';
      setErrors((err) => ({
        ...err,
        amount: isOverflow ? '金额超过3000，请联系后台客服咨询' : (val && !AMOUNT_RE.test(val)) ? '请输入有效数字，如：99 或 99.90' : '',
      }));
    } else {
      clearTimeout(hintTimer.current);
      setAmountHint('只能输入阿拉伯数字（0-9）和小数点');
      hintTimer.current = setTimeout(() => setAmountHint(''), 1500);
    }
  };

  const handleEmailBlur = () => {
    if (form.email && !EMAIL_RE.test(form.email)) {
      setErrors((err) => ({ ...err, email: '邮箱格式不正确，如：name@example.com' }));
    } else {
      setErrors((err) => ({ ...err, email: '' }));
    }
  };


  const range = useMemo(() => getRange(form.amount), [form.amount]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const amountMatched = matchProduct(form.amount);
    const amountErr = !form.amount ? '请填写支付金额'
      : !AMOUNT_RE.test(form.amount) ? '请输入有效数字，如：99 或 99.90'
      : amountMatched === 'overflow' ? '金额超过3000，请联系后台客服咨询'
      : '';
    const emailErr = !form.email ? '请填写收票邮箱' : !EMAIL_RE.test(form.email) ? '邮箱格式不正确，如：name@example.com' : '';

    if (amountErr || emailErr) {
      setErrors({ amount: amountErr, email: emailErr });
      return;
    }

    const uid = form.email;
    const productId = form.productId;
    const paymentDomain = getPaymentDomain();
    const desc = JSON.stringify({
      type: 'other',
      orderId: form.orderId,
      invoiceInfo: form.invoiceInfo,
      amount: form.amount,
      email: form.email,
    });

    setLoading(true);
    setLoadingStep('fetching');

    try {
      let response;

      if (paymentMethod === 'paynow') {
        response = await createPayNowPayment({ uid, product_id: productId, shopperName: uid, domain: paymentDomain, desc });
      } else if (paymentMethod === 'usdt') {
        response = await createCryptoPayment({ uid, product_id: productId, domain: paymentDomain, email: uid, desc });
      } else {
        const channel = getChannelNumber(paymentMethod);
        response = await getPaymentUrl({ uid, product_id: productId, domain: paymentDomain, channel, desc });
      }

      const redirectUrl = getRedirectUrlByMethod(paymentMethod, response);

      if (isPaymentRequestSuccessful(paymentMethod, response, redirectUrl)) {
        setLoadingStep('redirecting');
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            window.location.assign(redirectUrl);
          });
        });
      } else {
        setLoading(false);
        setLoadingStep('fetching');
        alert(response?.message || '获取支付链接失败，请重试');
      }
    } catch (error) {
      setLoading(false);
      setLoadingStep('fetching');
      alert('支付请求失败，请重试');
    }
  };

  const isPayDisabled = loading || !form.productId;


  return (
    <div className="mt-6 border border-line rounded-2xl bg-white">
      {loading && (
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-panel p-8 text-center">
            <div className="w-12 h-12 mx-auto mb-4 border-4 border-line border-t-black rounded-full animate-spin" />
            <h3 className="text-xl font-bold text-ink mb-2">
              {loadingStep === 'redirecting' ? '正在跳转支付页面' : '正在获取支付链接'}
            </h3>
            <p className="text-sm text-muted leading-relaxed">
              {loadingStep === 'redirecting'
                ? '支付链接已准备完成，正在为您打开支付页面，请不要关闭当前页面。'
                : '正在为您获取支付链接，请稍候，不要重复点击。'}
            </p>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-12 max-w-xl">

        <button
          onClick={() => navigate('/after-sales')}
          className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink transition-colors mb-8"
        >
          <ChevronLeft className="w-4 h-4" />
          售后服务
        </button>

        <h2 className="text-xl font-bold text-ink mb-1">其他凭证申请</h2>
        <p className="text-muted text-sm mb-8">此服务为其他凭证申请，按下方金额区间收取服务费；免费 Invoice 请联系右下角客服。</p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">

          <div>
            <label htmlFor="voucher-orderId" className="block text-sm font-bold text-ink mb-2">订单号 / 交易号</label>
            <input
              type="text"
              id="voucher-orderId"
              name="orderId"
              value={form.orderId}
              onChange={handleChange}
              required
              placeholder="请输入您的订单号或交易号"
              className="w-full border border-line rounded-2xl px-4 py-3.5 text-sm text-ink placeholder-gray-300 focus:outline-none focus:border-brand-line transition-colors"
            />
            <p className="mt-2 text-xs text-muted leading-relaxed">
              如果后台没有查询到您的支付信息，将不会开具凭证。请勿虚假填写。
            </p>
          </div>

          <div>
            <label htmlFor="voucher-invoiceInfo" className="block text-sm font-bold text-ink mb-2">开票信息</label>
            <textarea
              id="voucher-invoiceInfo"
              name="invoiceInfo"
              value={form.invoiceInfo}
              onChange={handleChange}
              required
              rows={4}
              placeholder={'请填写您需要的凭证信息，如：\n公司名称：xxx有限公司\n税号：91xxxxxxxxxxxxxxxxxx'}
              className="w-full border border-line rounded-2xl px-4 py-3.5 text-sm text-ink placeholder-gray-300 focus:outline-none focus:border-brand-line transition-colors resize-none"
            />
            {(
              <p className="mt-2 text-xs text-muted leading-relaxed">
                项目名称为 <span className="font-bold text-muted">现代服务 / 技术服务</span>
              </p>
            )}
          </div>

          <div>
            <label htmlFor="voucher-amount" className="block text-sm font-bold text-ink mb-2">支付金额</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-muted font-bold">
                ¥
              </span>
              <input
                type="text"
                id="voucher-amount"
              name="amount"
                value={form.amount}
                onChange={handleAmountChange}
                required
                placeholder="0.00"
                className={`w-full border-2 rounded-2xl pl-8 pr-4 py-3.5 text-sm text-ink placeholder-gray-300 focus:outline-none transition-colors ${errors.amount ? 'border-red-300 focus:border-red-400' : 'border-line focus:border-brand-line'}`}
              />
            </div>
            {amountHint && (
              <p className="mt-1.5 text-xs text-brand font-medium">{amountHint}</p>
            )}
            {errors.amount && (
              <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.amount}</p>
            )}
            <p className="mt-2 text-xs text-red-400 leading-relaxed font-medium">
              请按实际支付金额填写，不可虚报或随意填写，我们将以订单记录为准核实金额。
            </p>
          </div>

          {/* 其他凭证：动态服务费卡片 */}
          {range && !errors.amount && (
            <div className="bg-brand-soft border-2 border-brand-line rounded-2xl px-5 py-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold text-ink">开票服务费</p>
                <div className="flex items-center gap-1.5">
                  <CreditCard className="w-4 h-4 text-brand" />
                  <span className="text-xl font-bold text-brand">¥{range.fee.toFixed(2)}</span>
                </div>
              </div>
              <p className="text-xs text-muted leading-relaxed">
                您填写的金额属于 <span className="font-bold text-muted">{range.label}</span> 区间，按区间最高值 <span className="font-bold text-muted">¥{range.upper}</span> 的 4% 计算服务费。
              </p>
            </div>
          )}

          <div>
            <label htmlFor="voucher-email" className="block text-sm font-bold text-ink mb-2">收票邮箱</label>
            <input
              type="text"
              id="voucher-email"
              name="email"
              value={form.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              required
              placeholder="invoice@example.com"
              className={`w-full border-2 rounded-2xl px-4 py-3.5 text-sm text-ink placeholder-gray-300 focus:outline-none transition-colors ${errors.email ? 'border-red-300 focus:border-red-400' : 'border-line focus:border-brand-line'}`}
            />
            {errors.email && (
              <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.email}</p>
            )}
            <p className="mt-2 text-xs text-red-400 leading-relaxed font-medium">
              请确保能接收到邮件，票据将通过邮件向您发送。支付成功后，网页将默认以此邮箱作为您的登录邮箱。
            </p>
          </div>

          {/* 支付方式选择 */}
          <div>
            <label className="block text-xs font-bold text-muted uppercase tracking-widest mb-3">选择支付方式</label>
            <div className="grid grid-cols-2 md:grid-cols-12 gap-2.5">
              {paymentOptions.map((option) => {
                const isSelected = paymentMethod === option.value;
                const isAlipay = option.value === 'alipay';
                return (
                  <label
                    key={option.value}
                    className={`${option.gridClass} ${getPaymentCardClasses(option.value)} ${
                      isAlipay ? 'min-h-[72px] p-2.5' : 'min-h-[58px] p-2'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value={option.value}
                      checked={isSelected}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="sr-only"
                    />
                    <div className={`absolute top-2.5 right-2.5 w-4 h-4 rounded-full border flex items-center justify-center transition-all duration-300 ${
                      isSelected
                        ? 'border-brand bg-brand'
                        : 'border-line bg-white'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-transparent'}`} />
                    </div>
                    {option.recommended && (
                      <span className="absolute top-2.5 left-2.5 text-[9px] font-bold text-accent bg-white/90 border border-accent-line px-1.5 py-0.5 rounded-full">
                        推荐
                      </span>
                    )}
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <div className={`mb-1.5 rounded-xl ${isAlipay ? 'bg-white/90 shadow-sm px-2.5 py-1.5' : 'bg-canvas px-2 py-1.5'}`}>
                        <img src={paymentIcons[option.value]} alt={option.label} className={`${option.iconClass} object-contain`} />
                      </div>
                      <span className={`font-semibold text-ink leading-none ${isAlipay ? 'text-sm' : 'text-xs'}`}>{option.label}</span>
                      {option.subLabel && (
                        <span className={`mt-1 leading-none ${isAlipay ? 'text-[10px] text-accent font-medium' : 'text-[10px] text-muted'}`}>
                          {option.subLabel}
                        </span>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            disabled={isPayDisabled}
            className={`mt-2 w-full py-4 font-bold rounded-2xl transition-all duration-200 flex items-center justify-center gap-2 ${
              isPayDisabled
                ? 'bg-gray-200 text-muted cursor-not-allowed'
                : 'bg-brand text-white hover:bg-brand-hover active:scale-95'
            }`}
          >
            {loading ? '处理中...' : range && !errors.amount ? (
              <>
                <CreditCard className="w-4 h-4" />
                立即支付 ¥{range.fee.toFixed(2)}
              </>
            ) : (
              '提交申请'
            )}
          </button>

        </form>
      </div>
    </div>
  );
}


export default function Invoice() {
  const [showOther, setShowOther] = useState(false);
  return (
    <div className="page-shell">
      <PageHeading Icon={Receipt} eyebrow="INVOICE · 订单凭证" title="Invoice，免费领取" description="联系右下角在线客服，提供订单号即可申请。" />
      <div className="grid md:grid-cols-[1.3fr_1fr] gap-5">
        <section className="page-card">
          <span className="page-icon mb-5"><FileText /></span>
          <h2 className="text-xl font-bold mb-3">把订单号发给大师兄</h2>
          <p className="text-sm text-muted mb-6">客服核对订单后，协助你领取 Invoice。无需在本页支付申请费用。</p>
          <ol className="space-y-5 text-sm">
            {['在历史订单中找到对应订单号', '点击右下角客服气泡，说明需要 Invoice', '按客服提示提供抬头及接收信息'].map((step, index) => <li key={step} className="flex gap-3 items-center"><span className="w-7 h-7 rounded-full bg-brand-soft text-brand flex items-center justify-center shrink-0">{index + 1}</span>{step}</li>)}
          </ol>
          <div className="flex flex-wrap gap-3 mt-7"><Link to="/orders" className="brand-purchase">查找订单号</Link><Link to="/customer-service" className="page-secondary"><MessageCircle size={18} />联系客服</Link></div>
        </section>
        <aside className="page-card bg-brand-soft flex flex-col justify-center">
          <p className="page-eyebrow">INVOICE</p><p className="text-5xl font-bold text-brand mb-4">免费</p><p className="text-sm text-muted">订单核对、领取方式及所需资料，由客服为你确认。</p>
        </aside>
      </div>
      <div className="mt-8 border-t border-line pt-6">
        <button onClick={() => setShowOther(!showOther)} aria-expanded={showOther} aria-controls="other-voucher" className="text-sm text-muted flex items-center gap-2">其他凭证需求（另收服务费）<ArrowUpRight size={16} /></button>
        {showOther && <div id="other-voucher"><PaidVoucherForm /></div>}
      </div>
    </div>
  );
}
